import axios, { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
})

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('adminToken')
  if (token) {
    if (!config.headers) config.headers = new AxiosHeaders()
    ;(config.headers as AxiosHeaders).set('Authorization', `Bearer ${token}`)
  }
  return config
})

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    if (error.response?.status === 401 && !original._retry && !original.url?.includes('/auth/login')) {
      original._retry = true
      try {
        const refreshToken = localStorage.getItem('adminRefreshToken')
        if (!refreshToken) throw new Error('no refresh')
        const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken }, { withCredentials: true })
        localStorage.setItem('adminToken', res.data.data.accessToken)
        const headers = AxiosHeaders.from(original.headers ?? {})
        headers.set('Authorization', `Bearer ${res.data.data.accessToken}`)
        original.headers = headers
        return api(original)
      } catch {
        localStorage.removeItem('adminToken')
        localStorage.removeItem('adminRefreshToken')
        window.location.href = '/login'
        return Promise.reject(error)
      }
    }
    return Promise.reject(error)
  }
)

export const setTokens = (access: string, refresh: string) => {
  localStorage.setItem('adminToken', access)
  localStorage.setItem('adminRefreshToken', refresh)
}

export const clearTokens = () => {
  localStorage.removeItem('adminToken')
  localStorage.removeItem('adminRefreshToken')
}

interface ApiResp<T> { success: boolean; data?: T; message?: string }

export const authApi = {
  login: (email: string, password: string) =>
    api.post<ApiResp<{ user: any; accessToken: string; refreshToken: string }>>('/auth/login', { email, password }),
  loginWithLink: (token: string) =>
    api.post<ApiResp<{ user: any; accessToken: string; refreshToken: string }>>('/auth/login-link', { token }),
  getProfile: () => api.get<ApiResp<any>>('/auth/profile'),
  logout: () => api.post<ApiResp<null>>('/auth/logout')
}

export interface CreatedUserResult {
  user: any
  loginLink: string | null
  loginLinkExpiresAt: string | null
  email: { sent: boolean; mode: 'smtp' | 'console'; error?: string } | null
}

export interface LoginLinkResult {
  user: any
  loginLink: string
  loginLinkExpiresAt: string
  email: { sent: boolean; mode: 'smtp' | 'console'; error?: string }
}

export const adminApi = {
  // Dashboard
  dashboard: () => api.get<ApiResp<any>>('/admin/dashboard'),

  // تنظیمات عمومی
  getSettings: () => api.get<ApiResp<any[]>>('/admin/settings'),
  setSetting: (key: string, value: string, description?: string) =>
    api.post<ApiResp<any>>('/admin/settings', { key, value, description }),

  // محصولات
  getProducts: (params?: Record<string, any>) =>
    api.get<ApiResp<{ products: any[]; pagination: any }>>('/products', { params: { ...params, limit: 100, all: 'true' } }),
  createProduct: (data: Record<string, any>) => api.post<ApiResp<any>>('/products', data),
  updateProduct: (id: string, data: Record<string, any>) => api.put<ApiResp<any>>(`/products/${id}`, data),
  updateMarkup: (id: string, markupPercent: number) => api.put<ApiResp<any>>(`/products/${id}/markup`, { markupPercent }),
  // درصد منفی خرید (کسر خرید) اختصاصی هر محصول
  updateBuyback: (id: string, buybackPercent: number) => api.put<ApiResp<any>>(`/products/${id}/buyback`, { buybackPercent }),
  deleteProduct: (id: string) => api.delete<ApiResp<any>>(`/products/${id}`),
  getPriceHistory: (id: string) => api.get<ApiResp<any[]>>(`/products/${id}/price-history`),

  // درصد سود پیش‌فرض (طلا و نقره)
  getDefaultMarkups: () => api.get<ApiResp<Record<string, number>>>('/admin/markups/default'),
  updateDefaultMarkup: (type: string, category: string, markup: number) =>
    api.post<ApiResp<any>>('/admin/markups/default', { type, category, markup }),
  bulkUpdateMarkups: (markups: Record<string, number>) =>
    api.post<ApiResp<any>>('/admin/markups/bulk', { markups }),
  applyMarkupsToProducts: () => api.post<ApiResp<{ updated: number; total: number }>>('/admin/markups/apply-to-products'),

  // قیمت خام طلا و نقره
  getPricing: () => api.get<ApiResp<any>>('/admin/pricing'),
  updatePricing: (data: Record<string, any>) => api.post<ApiResp<any>>('/admin/pricing', data),
  refreshPrices: () => api.post<ApiResp<{ updated: number; errors: string[] }>>('/admin/prices/update'),
  currentPrices: () => api.get<ApiResp<any>>('/prices/current'),

  // سفارش‌ها
  getOrders: (params?: Record<string, any>) =>
    api.get<ApiResp<{ orders: any[]; pagination: any }>>('/orders/admin/all', { params }),
  getOrder: (id: string) => api.get<ApiResp<any>>(`/orders/${id}`),
  updateOrderStatus: (id: string, status: string) =>
    api.put<ApiResp<any>>(`/orders/admin/${id}/status`, { status }),
  getOrderStats: (params?: Record<string, any>) => api.get<ApiResp<any>>('/orders/admin/stats', { params }),

  // کاربران
  getUsers: (params?: Record<string, any>) =>
    api.get<ApiResp<{ users: any[]; pagination: any }>>('/admin/users', { params }),
  updateUser: (id: string, data: Record<string, any>) => api.patch<ApiResp<any>>(`/admin/users/${id}`, data),
  // ساخت کاربر جدید توسط مدیر (نام کاربری = ایمیل) + ارسال لینک ورود
  createUser: (data: {
    email: string
    password: string
    firstName: string
    lastName: string
    phone?: string
    role?: 'USER' | 'ADMIN'
    sendLoginLink?: boolean
  }) => api.post<ApiResp<CreatedUserResult>>('/admin/users', data),
  // ارسال/ارسال‌مجدد لینک ورود اختصاصی کاربر
  sendUserLoginLink: (id: string) =>
    api.post<ApiResp<LoginLinkResult>>(`/admin/users/${id}/login-link`),
  // تعیین رمز عبور جدید برای کاربر
  resetUserPassword: (id: string, password: string) =>
    api.post<ApiResp<any>>(`/admin/users/${id}/password`, { password }),

  // گزارش‌ها
  getSalesReport: (params?: Record<string, any>) => api.get<ApiResp<any>>('/admin/reports/sales', { params }),
  getUserStats: () => api.get<ApiResp<any>>('/admin/reports/users')
}

/** وضعیت کنترل معاملات (دکمه‌های استاپ) */
export interface TradingStatus {
  sellStopped: boolean
  buyStopped: boolean
  pricesHidden: boolean
  anyStopped: boolean
  updatedAt: string | null
}

export interface LedgerMetal {
  metal: 'GOLD' | 'SILVER'
  soldGrams: number
  boughtGrams: number
  netGrams: number
  soldAmount: number
  boughtAmount: number
  cashNet: number
  pricePerGram: number
  metalValue: number
  balance: number
  position: 'POSITIVE' | 'NEGATIVE'
}

export interface LedgerEntryRow {
  id: string
  date: string
  source: 'ORDER' | 'MANUAL'
  kind: 'BUY' | 'SELL' | 'MANUAL'
  title: string
  orderNumber?: string
  customer?: string
  status?: string
  goldGrams: number
  silverGrams: number
  totalGrams: number
  amount: number
  cashDelta: number
  note?: string
}

export interface LedgerSummary extends LedgerMetal {
  cashNet: number
  metalValue: number
  balance: number
  position: 'POSITIVE' | 'NEGATIVE'
  orderCount: number
  buyOrderCount: number
  sellOrderCount: number
  totalSoldAmount: number
  totalBoughtAmount: number
  byStatus: Record<string, number>
  metals: LedgerMetal[]
  priceSource: string
}

export interface LedgerResult {
  summary: LedgerSummary
  entries: LedgerEntryRow[]
}

/** کنترل معاملات (استاپ خرید/فروش) */
export const tradingApi = {
  getStatus: () => api.get<ApiResp<TradingStatus>>('/admin/trading'),
  updateStatus: (data: { sellStopped?: boolean; buyStopped?: boolean }) =>
    api.post<ApiResp<TradingStatus>>('/admin/trading', data)
}

/** تراز و دفتر معاملات */
export const ledgerApi = {
  get: (params?: Record<string, any>) => api.get<ApiResp<LedgerResult>>('/admin/ledger', { params }),
  createEntry: (data: {
    kind: 'CASH' | 'METAL'
    metal?: 'GOLD' | 'SILVER'
    direction: 'IN' | 'OUT'
    amount: number
    note?: string
  }) => api.post<ApiResp<any>>('/admin/ledger/entries', data),
  deleteEntry: (id: string) => api.delete<ApiResp<any>>(`/admin/ledger/entries/${id}`)
}

export default api
